import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  TrendingDown,
  Scissors,
  Calculator,
  BarChart3,
  Zap,
  Clock,
  Leaf,
  ShoppingCart,
  Scale,
  FileSpreadsheet,
  Lightbulb,
  BookOpen,
  List,
  Target,
  ThermometerSun,
  RefreshCw,
} from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Cual es el porcentaje ideal de food cost en un restaurante?',
    a: 'El food cost ideal para la mayoria de restaurantes esta entre el 25% y el 32% de la facturacion. Restaurantes de alta cocina pueden permitirse un 35-38% porque compensan con tickets altos, mientras que fast food y cadenas suelen operar con un 22-28%. Lo importante es conocer tu cifra exacta y controlarla, no simplemente asumir que "esta bien". Con PLEKO puedes monitorizar el food cost de cada receta en tiempo real.',
  },
  {
    q: '¿Como puedo reducir costes sin bajar la calidad de los platos?',
    a: 'Las estrategias mas efectivas son: 1) Optimizar el gramaje por racion con bascula (muchos restaurantes sirven un 15-20% mas de lo necesario). 2) Aprovechar productos de temporada, que son mas baratos y de mejor calidad. 3) Negociar con proveedores usando datos de precios de mercado. 4) Reducir mermas con tecnicas de aprovechamiento integral. 5) Aplicar ingenieria de menu para destacar platos rentables. Ninguna de estas acciones implica usar peores ingredientes.',
  },
  {
    q: '¿Cada cuanto debo revisar los costes de mi restaurante?',
    a: 'Los precios de compra deben revisarse con cada albaran o factura (idealmente semanal). El food cost global debe analizarse mensualmente como minimo. Los precios de la carta deben revisarse trimestralmente. Y la estructura de costes completa (personal, alquiler, energia) debe auditarse cada 6 meses. Con un software como PLEKO, la revision de food cost es automatica y continua.',
  },
  {
    q: '¿Que partida de gastos es la mas facil de reducir?',
    a: 'La materia prima es la partida donde mas rapido se ven resultados, porque depende directamente de tus decisiones de compra, menu y porciones. Puedes reducir el food cost entre un 3% y un 8% en los primeros meses solo con escandallos actualizados, control de mermas y negociacion con proveedores. Reducir personal o alquiler es mas lento y complejo.',
  },
  {
    q: '¿Es rentable invertir en un software de gestion de costes?',
    a: 'Si. Un restaurante que factura 30.000 euros al mes y reduce su food cost del 34% al 29% ahorra 1.500 euros mensuales en materia prima. El plan BASIC de PLEKO cuesta 49 euros/mes, lo que supone un ROI de 30x en el primer mes. Ademas, ahorras entre 4 y 8 horas semanales de gestion manual.',
  },
  {
    q: '¿Como afecta la estacionalidad a los costes del restaurante?',
    a: 'Los productos fuera de temporada cuestan entre un 30% y un 100% mas que en su momento optimo, y ademas pierden calidad. Adaptar tu carta a la temporada es una de las formas mas efectivas de reducir costes y mejorar la calidad percibida. PLEKO incluye un calendario de temporada con 86 productos espanoles para facilitar esta planificacion.',
  },
  {
    q: '¿Que diferencia hay entre food cost y coste total del restaurante?',
    a: 'El food cost solo incluye el coste de la materia prima (ingredientes). El coste total del restaurante incluye ademas personal (salarios, SS), alquiler, suministros (luz, agua, gas), marketing, mantenimiento, seguros y financiacion. Un restaurante rentable controla todas estas partidas, pero el food cost es la mas variable y la que mas rapido puedes optimizar.',
  },
  {
    q: '¿Como controlar costes en un restaurante con mucho personal?',
    a: 'El coste de personal es la segunda partida mas grande (25-35% de facturacion). Las claves son: 1) Control horario riguroso para evitar horas extra innecesarias. 2) Planificacion de turnos basada en prevision de demanda. 3) Formacion cruzada del equipo para cubrir puestos. 4) Seguimiento de productividad (cubiertos/hora por cocinero). PLEKO incluye control horario legal integrado en los planes PRO y SCALE.',
  },
];

/* ─── Strategy data ─────────────────────────────────── */

const strategies = [
  { id: 'estrategia-1', num: 1, title: 'Escandallos actualizados en tiempo real', savings: '2.000-5.000', icon: Calculator },
  { id: 'estrategia-2', num: 2, title: 'Negociacion inteligente con proveedores', savings: '1.500-4.000', icon: ShoppingCart },
  { id: 'estrategia-3', num: 3, title: 'Control de mermas y desperdicios', savings: '1.000-3.000', icon: Scissors },
  { id: 'estrategia-4', num: 4, title: 'Ingenieria de menu basada en datos', savings: '2.000-6.000', icon: BarChart3 },
  { id: 'estrategia-5', num: 5, title: 'Optimizar la carta: menos platos, mas rentabilidad', savings: '1.000-3.000', icon: Target },
  { id: 'estrategia-6', num: 6, title: 'Control de porciones con gramaje estandar', savings: '1.500-4.000', icon: Scale },
  { id: 'estrategia-7', num: 7, title: 'Inventario y rotacion FIFO/FEFO', savings: '800-2.500', icon: RefreshCw },
  { id: 'estrategia-8', num: 8, title: 'Reducir el coste energetico', savings: '1.200-3.500', icon: ThermometerSun },
  { id: 'estrategia-9', num: 9, title: 'Aprovechar productos de temporada', savings: '1.000-3.000', icon: Leaf },
  { id: 'estrategia-10', num: 10, title: 'Control horario eficiente', savings: '2.000-6.000', icon: Clock },
  { id: 'estrategia-11', num: 11, title: 'Digitalizar la gestion: adios al papel', savings: '500-1.500', icon: FileSpreadsheet },
  { id: 'estrategia-12', num: 12, title: 'Revisar precios de la carta cada trimestre', savings: '1.500-4.000', icon: TrendingDown },
];

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'por-que', label: 'Por que el control de costes marca la diferencia' },
  { id: 'centros-coste', label: 'Los 5 centros de coste de un restaurante' },
  { id: 'tabla-porcentajes', label: 'Porcentaje ideal de cada partida' },
  ...strategies.map((s) => ({ id: s.id, label: `Estrategia ${s.num}: ${s.title}` })),
  { id: 'tabla-resumen', label: 'Tabla resumen: impacto de cada estrategia' },
  { id: 'caso-practico', label: 'Caso practico: -18% en 6 meses' },
  { id: 'herramientas', label: 'Herramientas para controlar costes' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const howToJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Como controlar los costes de un restaurante en 12 pasos',
  description:
    'Guia practica con 12 estrategias para reducir los costes de un restaurante sin perder calidad. Incluye ahorro estimado por estrategia y caso practico real.',
  totalTime: 'P6M',
  step: strategies.map((s, i) => ({
    '@type': 'HowToStep',
    position: i + 1,
    name: s.title,
    text: `Estrategia ${s.num} para reducir costes en hosteleria. Ahorro estimado: ${s.savings} euros/año.`,
    url: `https://pleko.app/control-costes-restaurante#${s.id}`,
  })),
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Control de Costes en Restaurantes',
      item: 'https://pleko.app/control-costes-restaurante',
    },
  ],
};

/* ─── Cost centers data ──────────────────────────────── */

const costCenters = [
  { name: 'Materia prima (food cost)', pctIdeal: '25-32%', desc: 'Ingredientes, bebidas, productos de limpieza de cocina', color: 'emerald' },
  { name: 'Personal', pctIdeal: '25-35%', desc: 'Salarios, Seguridad Social, formacion, uniformes', color: 'blue' },
  { name: 'Alquiler y amortizaciones', pctIdeal: '8-12%', desc: 'Alquiler del local, amortizacion de equipamiento, reformas', color: 'violet' },
  { name: 'Suministros y energia', pctIdeal: '4-7%', desc: 'Electricidad, gas, agua, combustible', color: 'amber' },
  { name: 'Otros gastos', pctIdeal: '8-15%', desc: 'Marketing, seguros, contabilidad, mantenimiento, impuestos', color: 'slate' },
];

/* ─── Component ──────────────────────────────────────── */

export default function ControlCostesRestaurantePage() {
  return (
    <>
      <Helmet>
        <title>Control de Costes en Restaurantes: 12 Estrategias para Reducir Gastos [2026] | PLEKO</title>
        <meta
          name="description"
          content="Guia practica con 12 estrategias para controlar los costes de tu restaurante y reducir gastos sin perder calidad. Incluye caso practico real con un 18% de ahorro en 6 meses."
        />
        <meta
          name="keywords"
          content="control costes restaurante, reducir costes restaurante, gestion costes hosteleria, ahorrar hosteleria, optimizar gastos restaurante, como reducir gastos restaurante, control de gastos restauracion, bajar costes cocina, ahorro restaurante, costes fijos restaurante"
        />
        <link rel="canonical" href="https://pleko.app/control-costes-restaurante" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/control-costes-restaurante" />
        <meta
          property="og:title"
          content="Control de Costes en Restaurantes: 12 Estrategias para Reducir Gastos [2026]"
        />
        <meta
          property="og:description"
          content="12 estrategias probadas para reducir los costes de tu restaurante sin perder calidad. Caso practico real: -18% en 6 meses."
        />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Control de Costes en Restaurantes: 12 Estrategias [2026] | PLEKO"
        />
        <meta
          name="twitter:description"
          content="12 estrategias probadas para reducir costes en hosteleria. Ahorro estimado: 15.000-45.000 euros/año."
        />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(howToJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* ═══════════ HERO ═══════════ */}
        <section className="bg-gradient-to-b from-emerald-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-6">
                <TrendingDown className="w-4 h-4" />
                Guia practica de control de costes — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Control de Costes en Restaurantes:{' '}
                <span className="text-emerald-600">12 Estrategias para Reducir Gastos</span> sin Perder Calidad
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                El 60% de los restaurantes que cierran no controlaban sus costes. Descubre las 12 estrategias que usan los restaurantes mas rentables para reducir gastos entre <strong>15.000 y 45.000 euros al año</strong> sin sacrificar la calidad de sus platos.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#estrategia-1"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-200"
                >
                  Ver las 12 estrategias
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Probar PLEKO gratis
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ CONTENT ═══════════ */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-emerald-600" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-emerald-600 hover:text-emerald-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">
            {/* ═══════════ SECTION 1: POR QUE ═══════════ */}
            <h2 id="por-que" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Por que el control de costes es la diferencia entre sobrevivir y prosperar
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              En la hosteleria espanola, el margen neto medio de un restaurante se situa entre el <strong>5% y el 15%</strong> de la facturacion. Eso significa que por cada 100 euros que facturas, te quedan entre 5 y 15 euros de beneficio. Un solo punto porcentual de descontrol en los costes puede representar la diferencia entre un negocio rentable y uno que pierde dinero.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Segun datos del sector, el <strong>60% de los restaurantes que cierran en los primeros 3 anos</strong> no llevaban un control de costes sistematico. No es que sirvieran mala comida — es que no sabian exactamente cuanto les costaba producir cada plato, cuanto desperdiciaban y donde se les escapaba el margen.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              El control de costes no es recortar, sino <strong>optimizar</strong>. Se trata de saber exactamente donde va cada euro, identificar los puntos de fuga y actuar con datos en lugar de con intuicion. Los restaurantes mas rentables no son necesariamente los que mas facturan — son los que mejor controlan sus costes.
            </p>

            {/* ═══════════ SECTION 2: 5 CENTROS DE COSTE ═══════════ */}
            <h2 id="centros-coste" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Los 5 centros de coste de un restaurante
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Antes de optimizar, necesitas entender donde se va el dinero. Todo restaurante tiene cinco grandes partidas de gasto. Conocerlas es el primer paso para controlarlas:
            </p>
            <div className="space-y-4 mb-10">
              {costCenters.map(({ name, pctIdeal, desc }) => (
                <div key={name} className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="flex-shrink-0 w-16 text-center">
                    <p className="text-lg font-bold text-emerald-600">{pctIdeal}</p>
                    <p className="text-xs text-slate-400">ideal</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{name}</h3>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 3: TABLA PORCENTAJES ═══════════ */}
            <h2 id="tabla-porcentajes" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Porcentaje ideal de cada partida sobre facturacion
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Esta tabla te permite comparar tus numeros con los de un restaurante sano. Si alguna partida supera el rango ideal, es una senal de alarma que requiere accion inmediata:
            </p>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Partida</th>
                    <th className="p-3 text-center font-medium">% ideal</th>
                    <th className="p-3 text-center font-medium">Alarma</th>
                    <th className="p-3 text-left font-medium">Accion si se supera</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Materia prima', '25-32%', '>35%', 'Revisar escandallos, mermas y proveedores'],
                    ['Personal', '25-35%', '>38%', 'Optimizar turnos, reducir horas extra'],
                    ['Alquiler', '8-12%', '>15%', 'Renegociar contrato o aumentar facturacion'],
                    ['Energia', '4-7%', '>9%', 'Auditar equipos, LED, programar encendidos'],
                    ['Otros gastos', '8-15%', '>18%', 'Revisar seguros, marketing, mantenimiento'],
                    ['Beneficio neto', '5-15%', '<5%', 'Aplicar las 12 estrategias de esta guia'],
                  ].map(([partida, ideal, alarma, accion], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{partida}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-emerald-600">{ideal}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-red-500">{alarma}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-sm">{accion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ═══════════ 12 STRATEGIES ═══════════ */}

            {/* ESTRATEGIA 1 */}
            <h2 id="estrategia-1" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">1</span>
                Escandallos actualizados en tiempo real
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El <Link to="/escandallo" className="text-emerald-600 hover:underline">escandallo</Link> es la herramienta mas basica y poderosa para controlar el food cost. Cada plato de tu carta debe tener una ficha con todos los ingredientes, cantidades, mermas y precios que te permite saber exactamente cuanto te cuesta producir cada racion.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El problema es que los precios de compra cambian constantemente. Un escandallo con precios de hace un mes ya miente. La solucion es usar un sistema que actualice todos los escandallos automaticamente cada vez que cambia un precio — exactamente lo que hace PLEKO con su OCR de facturas.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 2.000-5.000 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Solo con conocer el coste real de cada plato, la mayoria de restaurantes descubren entre 3 y 5 recetas con food cost descontrolado que estaban erosionando su margen sin saberlo.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 2 */}
            <h2 id="estrategia-2" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">2</span>
                Negociacion inteligente con proveedores
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              La mayoria de hosteleros negocian con sus proveedores sin datos. "Me parece caro" no es un argumento — "Mercabarna cotiza este producto a 8,50 euros/kg y tu me cobras 11,20 euros/kg" si lo es. Comparar tus precios de compra con los de mercados mayoristas te da poder de negociacion real.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Trabaja con al menos dos o tres proveedores por categoria de producto. No es necesario cambiar de proveedor, pero si que tu proveedor actual sepa que tienes alternativas y que monitorizas los precios del mercado.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 1.500-4.000 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Negociar con datos reales puede reducir los precios de compra entre un 5% y un 15% en las partidas principales. PLEKO compara tus precios con Mercabarna y Mercamadrid automaticamente.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 3 */}
            <h2 id="estrategia-3" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">3</span>
                Control de mermas y desperdicios
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Las <Link to="/mermas-cocina" className="text-emerald-600 hover:underline">mermas en cocina</Link> representan entre un 8% y un 15% del coste de materia prima. Hay mermas inevitables (pelar, deshuesar, limpiar), pero buena parte del desperdicio es evitable: producto mal almacenado, sobreproduccion, porciones excesivas y mala rotacion de stock.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Consulta nuestra <Link to="/tabla-mermas-alimentos" className="text-emerald-600 hover:underline">tabla de mermas por alimento</Link> para tener referencias fiables. Mide tus mermas reales pesando producto antes y despues de procesar. La diferencia entre la merma teorica y la real te indica cuanto margen tienes de mejora.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 1.000-3.000 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Reducir la merma evitable en un 3-5% en un restaurante que compra 10.000 euros/mes en materia prima supone 300-500 euros mensuales de ahorro directo.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 4 */}
            <h2 id="estrategia-4" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">4</span>
                Ingenieria de menu basada en datos
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              La <Link to="/ingenieria-de-menu" className="text-emerald-600 hover:underline">ingenieria de menu</Link> clasifica tus platos en cuatro categorias segun su rentabilidad y popularidad: Stars (rentables y vendidos), Plowhorses (vendidos pero poco rentables), Puzzles (rentables pero poco vendidos) y Dogs (ni rentables ni vendidos).
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Con esta clasificacion puedes tomar decisiones estrategicas: destacar Stars en la carta, subir precios de Plowhorses, promocionar Puzzles y eliminar o reformular Dogs. Es la forma mas inteligente de aumentar el margen sin subir los precios de forma generalizada.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 2.000-6.000 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Redirigir ventas desde platos Dogs hacia Stars puede mejorar el margen bruto entre un 3% y un 8% sin cambiar los precios de la carta.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 5 */}
            <h2 id="estrategia-5" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">5</span>
                Optimizar la carta: menos platos, mas rentabilidad
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Un error clasico de la hosteleria es tener cartas demasiado extensas. Cada plato adicional implica mas ingredientes en stock (con riesgo de caducidad), mas complejidad en cocina (mas errores, mas tiempo) y mas dificultad para controlar los costes. Las cartas mas rentables son las mas enfocadas.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Analiza tus datos de ventas: es probable que el 20% de tus platos genere el 80% de tu facturacion. Reduce la carta a los platos que realmente se venden y que son rentables. Un restaurante con 25 platos bien ejecutados es mas rentable que uno con 60 platos mediocres.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 1.000-3.000 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Menos platos = menos stock muerto, menos desperdicio y cocina mas eficiente. Un restaurante que paso de 55 a 30 platos redujo su merma un 40%.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 6 */}
            <h2 id="estrategia-6" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">6</span>
                Control de porciones con gramaje estandar
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Si cada cocinero emplata "a ojo", las porciones pueden variar entre un 15% y un 30% de una persona a otra. Eso significa que algunos clientes reciben un 30% mas de producto del que has calculado en tu escandallo — y tu food cost real es mucho peor de lo que crees.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              La solucion es definir el gramaje estandar de cada ingrediente por racion y usar bascula en cocina durante el emplatado. No se trata de pesar cada plato para siempre, sino de calibrar al equipo. Despues de unas semanas, el "ojo" de cada cocinero estara ajustado al gramaje correcto.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 1.500-4.000 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Estandarizar porciones reduce el sobrecoste por emplatar "de mas" entre un 5% y un 10%. En un restaurante con 100 cubiertos/dia, eso se traduce en cientos de euros al mes.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 7 */}
            <h2 id="estrategia-7" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">7</span>
                Inventario y rotacion FIFO/FEFO
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              FIFO (First In, First Out) y FEFO (First Expired, First Out) son los dos sistemas de rotacion de stock que todo restaurante deberia aplicar. El principio es simple: lo que llega primero (o caduca antes) se usa primero. Suena obvio, pero en la practica muchos restaurantes apilan producto nuevo encima del viejo y acaban tirando comida caducada.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Haz inventario fisico al menos una vez por semana. Compara el stock real con el teorico (lo que deberias tener segun compras y ventas). La diferencia son mermas ocultas, robos o errores de registro. Cada euro que tiras a la basura es un euro que sale de tu beneficio.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 800-2.500 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Un inventario bien gestionado reduce el desperdicio por caducidad entre un 30% y un 50%. Tambien detecta discrepancias que pueden indicar problemas mas graves.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 8 */}
            <h2 id="estrategia-8" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">8</span>
                Reducir el coste energetico
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              La energia es la partida de gastos que mas ha crecido en los ultimos anos. Un restaurante medio espanol gasta entre 1.500 y 3.000 euros al mes en electricidad y gas. La buena noticia es que con acciones sencillas puedes reducir este gasto entre un 15% y un 30%.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Las medidas con mayor impacto son: sustituir iluminacion por LED (ahorro del 60-80% en luz), programar el encendido de hornos y camaras (evitar tenerlos a maxima potencia sin necesidad), revisar el aislamiento de camaras frigorificas, y negociar la tarifa electrica con tu comercializadora. Un horno que se precalienta 30 minutos antes de lo necesario cada dia consume cientos de euros al ano innecesariamente.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 1.200-3.500 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    LED + programacion de equipos + revision de tarifa = reduccion del 15-25% en la factura energetica. Inversion recuperable en 3-6 meses.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 9 */}
            <h2 id="estrategia-9" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">9</span>
                Aprovechar productos de temporada
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Los productos fuera de temporada cuestan entre un 30% y un 100% mas que en su momento optimo, y ademas llegan con menos sabor y calidad. Adaptar tu carta a la temporada es una de las formas mas inteligentes de reducir costes y mejorar simultaneamente la calidad de tus platos.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Consulta nuestro <Link to="/calendario-temporada" className="text-emerald-600 hover:underline">calendario de productos de temporada</Link> con 86 productos espanoles (frutas, verduras, pescados, mariscos). Planifica cambios de carta trimestrales o mensuales que aprovechen lo mejor de cada estacion.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 1.000-3.000 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Sustituir 5-10 ingredientes fuera de temporada por alternativas en su momento optimo puede reducir el food cost de esos platos entre un 15% y un 40%.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 10 */}
            <h2 id="estrategia-10" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">10</span>
                Control horario eficiente
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El coste de personal es la segunda partida mas grande de un restaurante (25-35%). Ademas del cumplimiento legal (<Link to="/control-horario-hosteleria" className="text-emerald-600 hover:underline">RD-ley 8/2019</Link>), un buen control horario te permite detectar horas extra innecesarias, optimizar turnos segun la demanda prevista y calcular la productividad real de tu equipo.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Planifica los turnos basandote en datos historicos de ocupacion. Un lunes a mediodia no necesita la misma dotacion que un sabado noche. Ajustar la plantilla a la demanda real es la forma mas efectiva de controlar el coste de personal sin reducir sueldos ni despedir.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 2.000-6.000 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Eliminar 2-3 horas extra innecesarias por semana a un coste medio de 15 euros/hora supone 1.500-2.300 euros/ano. Con turnos optimizados, el ahorro es aun mayor.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 11 */}
            <h2 id="estrategia-11" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">11</span>
                Digitalizar la gestion: adios al papel
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Gestionar un restaurante con libretas, hojas de calculo y post-its es ineficiente y propenso a errores. La digitalizacion no es un lujo — es una necesidad para competir. Un sistema integrado que conecte escandallos, inventario, pedidos, horarios y ventas te da una vision completa de tu negocio y te permite tomar decisiones rapidas basadas en datos reales.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              El coste de no digitalizar es invisible pero real: horas perdidas buscando datos, errores de calculo que nadie detecta, oportunidades de ahorro que no ves porque no tienes la informacion organizada. Un software como PLEKO centraliza toda la gestion de costes en una sola plataforma.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 500-1.500 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Entre 4 y 8 horas semanales ahorradas en tareas manuales (actualizar precios, hacer inventario, calcular costes). A 15 euros/hora, son 3.000-6.000 euros en tiempo recuperado.
                  </p>
                </div>
              </div>
            </div>

            {/* ESTRATEGIA 12 */}
            <h2 id="estrategia-12" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center text-lg">12</span>
                Revisar precios de la carta cada trimestre
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Muchos restaurantes fijan los precios de la carta una vez al ano (o nunca los cambian). Pero si tus costes de materia prima suben un 10% y tus precios no se mueven, tu margen se ha reducido un 10%. Revisar los precios de venta al menos cada trimestre es esencial para mantener la rentabilidad.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Usa tu escandallo actualizado para <Link to="/calcular-precio-plato" className="text-emerald-600 hover:underline">calcular el precio de venta correcto</Link> de cada plato. No se trata de subir todo un 10% — se trata de ajustar cada plato individualmente segun su food cost real. Algunos platos necesitaran subir, otros pueden mantenerse o incluso bajar si el coste ha disminuido.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Ahorro estimado: 1.500-4.000 euros/ano</p>
                  <p className="text-emerald-700 text-sm mt-1">
                    Un ajuste del 3-5% en los precios de los platos mas vendidos (basado en datos de food cost) puede recuperar miles de euros de margen perdido por la inflacion.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ TABLA RESUMEN ═══════════ */}
            <h2 id="tabla-resumen" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Tabla resumen: impacto estimado de cada estrategia
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Aqui tienes el resumen de las 12 estrategias con su ahorro anual estimado para un restaurante tipo que factura entre 25.000 y 40.000 euros/mes:
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-center font-medium w-10">#</th>
                    <th className="p-3 text-left font-medium">Estrategia</th>
                    <th className="p-3 text-right font-medium">Ahorro anual</th>
                    <th className="p-3 text-center font-medium">Dificultad</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    [1, 'Escandallos en tiempo real', '2.000-5.000 euros', 'Baja'],
                    [2, 'Negociacion con proveedores', '1.500-4.000 euros', 'Media'],
                    [3, 'Control de mermas', '1.000-3.000 euros', 'Media'],
                    [4, 'Ingenieria de menu', '2.000-6.000 euros', 'Media'],
                    [5, 'Optimizar carta (menos platos)', '1.000-3.000 euros', 'Baja'],
                    [6, 'Control de porciones', '1.500-4.000 euros', 'Baja'],
                    [7, 'Rotacion FIFO/FEFO', '800-2.500 euros', 'Baja'],
                    [8, 'Reducir coste energetico', '1.200-3.500 euros', 'Media'],
                    [9, 'Productos de temporada', '1.000-3.000 euros', 'Baja'],
                    [10, 'Control horario eficiente', '2.000-6.000 euros', 'Media'],
                    [11, 'Digitalizar la gestion', '500-1.500 euros', 'Baja'],
                    [12, 'Revisar precios trimestralmente', '1.500-4.000 euros', 'Baja'],
                  ].map(([num, nombre, ahorro, dificultad], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 text-center font-bold text-emerald-600">{num}</td>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{nombre}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-emerald-600">{ahorro}</td>
                      <td className="p-3 border-b border-slate-100 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${dificultad === 'Baja' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {dificultad}
                        </span>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-emerald-50">
                    <td className="p-3" />
                    <td className="p-3 font-bold text-slate-800 text-base">TOTAL ESTIMADO</td>
                    <td className="p-3 text-right font-bold text-emerald-600 text-lg">16.000-45.500 euros</td>
                    <td className="p-3" />
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Nota importante</p>
                  <p className="text-amber-700 text-sm mt-1">
                    Los ahorros no son acumulativos al 100% — hay solapamientos entre estrategias. Un ahorro realista aplicando las 12 estrategias es del <strong>15-25% del gasto total</strong>, que para un restaurante medio supone entre 15.000 y 30.000 euros/ano.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ CASO PRACTICO ═══════════ */}
            <h2 id="caso-practico" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Caso practico: restaurante que redujo costes un 18% en 6 meses
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Un restaurante de cocina mediterranea en Barcelona con 60 cubiertos, facturando 35.000 euros/mes, tenia estos numeros antes de implementar un sistema de control de costes:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="p-5 bg-red-50 border border-red-100 rounded-xl">
                <h4 className="font-bold text-red-800 mb-3">Antes (sin control sistematico)</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><span className="text-red-400">x</span> Food cost: 36% (12.600 euros/mes)</li>
                  <li className="flex gap-2"><span className="text-red-400">x</span> Merma estimada: 12-14%</li>
                  <li className="flex gap-2"><span className="text-red-400">x</span> Escandallos: 15 de 48 platos (Excel)</li>
                  <li className="flex gap-2"><span className="text-red-400">x</span> Precios de carta: sin revisar en 8 meses</li>
                  <li className="flex gap-2"><span className="text-red-400">x</span> Horas extra semanales: 12h no controladas</li>
                  <li className="flex gap-2"><span className="text-red-400">x</span> Beneficio neto: 4,2%</li>
                </ul>
              </div>
              <div className="p-5 bg-green-50 border border-green-100 rounded-xl">
                <h4 className="font-bold text-green-800 mb-3">Despues (6 meses con PLEKO)</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Food cost: 29,5% (10.325 euros/mes)</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Merma real: 8%</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Escandallos: 48 de 48 platos (automaticos)</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Precios: revisados trimestralmente</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Horas extra: 3h/semana controladas</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Beneficio neto: 11,8%</li>
                </ul>
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center mb-10">
              <p className="text-lg font-bold text-emerald-800 mb-1">Resultado: 2.275 euros/mes de ahorro en materia prima + 7,6 puntos de beneficio neto</p>
              <p className="text-emerald-600 text-sm">Ahorro anual: 27.300 euros. Coste de PLEKO: 948 euros/ano (plan PRO). <strong>ROI: 28x</strong></p>
            </div>

            {/* ═══════════ HERRAMIENTAS ═══════════ */}
            <h2 id="herramientas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Herramientas para controlar los costes de tu restaurante
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Para aplicar las 12 estrategias necesitas herramientas que automaticen el trabajo repetitivo y te den datos fiables. Estas son las principales opciones:
            </p>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-slate-400" /> Excel / hojas de calculo
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Gratuito y familiar. Pero requiere actualizacion 100% manual, no escala con +30 recetas y los errores de formula pasan desapercibidos.
                </p>
                <ul className="space-y-1 text-sm text-slate-600">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Gratis</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">x</span>Todo manual</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">x</span>Errores frecuentes</li>
                </ul>
              </div>
              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-slate-400" /> Software generico
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  ERPs genericos o software de contabilidad. Cubren facturacion y contabilidad, pero no estan disenados para escandallos ni food cost.
                </p>
                <ul className="space-y-1 text-sm text-slate-600">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Bueno para contabilidad</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">x</span>Sin escandallos</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">x</span>Sin OCR de facturas</li>
                </ul>
              </div>
              <div className="border-2 border-emerald-300 bg-emerald-50 rounded-xl p-5">
                <h3 className="font-bold text-emerald-700 mb-3 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-emerald-500" /> PLEKO (especializado)
                </h3>
                <p className="text-sm text-slate-700 mb-3">
                  Disenado para control de costes en hosteleria. Escandallos automaticos, OCR de facturas, precios de mercado, mermas, control horario y carta digital.
                </p>
                <ul className="space-y-1 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Todo automatico</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />OCR + precios mercado</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Desde 49 euros/mes</li>
                </ul>
                <Link to="/software-escandallo" className="text-sm text-emerald-700 font-semibold hover:underline block mt-3">
                  Ver PLEKO en detalle →
                </Link>
              </div>
            </div>
          </div>

          {/* ═══════════ FAQ ═══════════ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Preguntas frecuentes sobre control de costes en restaurantes
            </h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4 flex-shrink-0">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* ═══════════ INTERNAL LINKS ═══════════ */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/food-cost', label: 'Food Cost: que es y como calcularlo' },
                { to: '/mermas-cocina', label: 'Mermas en cocina: tabla completa' },
                { to: '/tabla-mermas-alimentos', label: 'Tabla de mermas por alimento' },
                { to: '/rentabilidad-restaurante', label: 'Rentabilidad en restaurantes' },
                { to: '/calcular-precio-plato', label: 'Como calcular el precio de un plato' },
                { to: '/ingenieria-de-menu', label: 'Ingenieria de menu basada en datos' },
                { to: '/software-escandallo', label: 'Software de escandallo para restaurantes' },
                { to: '/calendario-temporada', label: 'Calendario de productos de temporada' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-800 hover:underline">
                  <ArrowRight className="w-3 h-3 flex-shrink-0" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* ═══════════ FINAL CTA ═══════════ */}
          <div className="mt-16 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-8 text-center text-white">
            <TrendingDown className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">
              Controla los costes de tu restaurante con PLEKO
            </h2>
            <p className="text-emerald-100 mb-6 max-w-xl mx-auto">
              Escandallos automaticos, OCR de facturas, precios de mercado, control de mermas y mucho mas. Todo en una plataforma disenada para hosteleria. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-emerald-700 font-bold rounded-xl hover:bg-emerald-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-emerald-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
